import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { evmToNibiAddress } from "../../evm/services/referralService";
import { getReferralConfig } from "../../evm/referralConfig";
import { getUserTokenBalance, getUserVaultDeposits } from "../../Pages/Trading/Vaults/VaultQueries";
import { getCollateralDenom } from "../../config/network";
import { AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import useTheme from "../../hooks/useTheme";
import { Skeleton, SkeletonLine } from "../utils/Skeleton";

/**
 * Recharts writes its colours as SVG *presentation attributes*, and `var()`
 * does not resolve there — so the chart cannot read the token layer the way
 * every other surface on this page does. These are the same values the tokens
 * carry, picked per theme instead: the accent and the up/down text pair lift
 * in dark, the neutrals follow `--line-2` / `--ink-3`.
 */
const chartPalette = (isDark) =>
  isDark
    ? { accent: "#8B5CF6", up: "#3FC47E", grid: "#262B35", axis: "#7C8695", dot: "#171A21" }
    : { accent: "#5B1DEE", up: "#1F8B58", grid: "#E8E9F0", axis: "#959595", dot: "#FFFFFF" };

// ─── Helpers ───

const formatCurrency = (value) => {
  const absValue = Math.abs(value);
  let decimals = 2;
  if (absValue > 0 && absValue < 1) {
    while (decimals < 6 && Number(absValue.toFixed(decimals)) === 0) decimals++;
  }
  return absValue.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const formatPct = (value) => Math.abs(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── Inline Icons ───

const TrendUpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: "var(--up)" }} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const TrendDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: "var(--down)" }} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
  </svg>
);

const InfoTip = ({ id, content }) => (
  <span
    data-tooltip-id="pnl-overview-tooltip"
    data-tooltip-content={content}
    className="cursor-help"
  >
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: "var(--ink-3)" }} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  </span>
);

// ─── Custom Chart Tooltip ───

const ChartTooltipContent = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;

  return (
    <div
      className="px-4 py-3 text-[12px]"
      style={{
        background: "var(--surface)",
        borderRadius: "var(--r-inner)",
        boxShadow: "var(--float)",
      }}
    >
      <p className="font-medium mb-1.5" style={{ color: "var(--ink-3)" }}>
        {new Date(d.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
      </p>
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-4">
          <span style={{ color: "var(--ink-3)" }}>Realized PnL</span>
          <span className="font-semibold" style={{ color: d.realized >= 0 ? "var(--up)" : "var(--down)" }}>
            {d.realized >= 0 ? "" : "-"}${formatCurrency(Math.abs(d.realized))}
          </span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span style={{ color: "var(--ink-3)" }}>Unrealized PnL</span>
          <span className="font-semibold" style={{ color: d.unrealized >= 0 ? "var(--up)" : "var(--down)" }}>
            {d.unrealized >= 0 ? "" : "-"}${formatCurrency(Math.abs(d.unrealized))}
          </span>
        </div>
        {d.volume > 0 && (
          <div className="flex items-center justify-between gap-4 pt-1" style={{ borderTop: "1px solid var(--line-2)" }}>
            <span style={{ color: "var(--ink-3)" }}>Volume</span>
            <span className="font-semibold" style={{ color: "var(--ink)" }}>${formatCurrency(d.volume)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Stat Card ───

const StatCard = ({ label, tooltip, children, accent }) => (
  <div
    className="px-4 sm:px-5 py-4"
    style={{
      background: "var(--surface)",
      borderRadius: "var(--r-panel)",
      boxShadow: "var(--card)",
    }}
  >
    <div className="flex items-center gap-1.5 mb-2">
      <span className="ce-eyebrow">{label}</span>
      {tooltip && <InfoTip content={tooltip} />}
    </div>
    {children}
  </div>
);

// ─── Skeletons ───

const ValueSkeleton = () => <SkeletonLine w={96} h={26} />;

/**
 * The whole overview, pending. Same header, same four cards, same chart box at
 * the same height — so when the keeper answers, the figures land in the slots
 * rather than pushing the trades table down the page.
 */
const OverviewSkeleton = () => (
  <div className="mb-6 space-y-4">
    <div className="flex items-center justify-between">
      <div>
        <SkeletonLine w={140} h={15} />
        <SkeletonLine w={180} h={10} style={{ marginTop: 8 }} />
      </div>
      <div className="flex items-center gap-5 sm:gap-7">
        {[0, 1].map((i) => (
          <div key={i} className="flex flex-col items-end gap-2">
            <SkeletonLine w={64} h={10} index={i} />
            <SkeletonLine w={84} h={15} index={i} />
          </div>
        ))}
      </div>
    </div>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="px-4 sm:px-5 py-4"
          style={{ background: "var(--surface)", borderRadius: "var(--r-panel)", boxShadow: "var(--card)" }}
        >
          <SkeletonLine w={78} h={10} index={i} />
          <SkeletonLine w={110} h={24} index={i} style={{ marginTop: 12 }} />
          <SkeletonLine w={62} h={10} index={i} style={{ marginTop: 10 }} />
        </div>
      ))}
    </div>

    <div
      className="px-4 sm:px-5 pt-4 pb-4"
      style={{ background: "var(--surface)", borderRadius: "var(--r-card)", boxShadow: "var(--card)" }}
    >
      <div className="flex items-center justify-between mb-3">
        <SkeletonLine w={190} h={13} />
        <SkeletonLine w={120} h={30} radius="var(--r-pill)" />
      </div>
      <div className="h-[200px] sm:h-[240px]">
        <Skeleton h="100%" radius="var(--r-inner)" />
      </div>
    </div>
  </div>
);

// ─── Main Component ───

const TotalPnL = ({ openTrades = [] }) => {
  const account = useSelector((state) => state.auth.account);
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const palette = chartPalette(isDark);

  // ─── State ───
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartRange, setChartRange] = useState("30D");
  const [portfolioStats, setPortfolioStats] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null); // USDC wallet balance
  const [vaultDeposits, setVaultDeposits] = useState(null); // current value of vault deposits
  const [walletLoading, setWalletLoading] = useState(true);

  // ─── Total margin from open trades (no RPC needed) ───
  const { totalMargin, tradeCount } = useMemo(() => {
    let margin = 0;
    const count = openTrades.filter((t) => t.is_open && t.market_index).length;
    openTrades.forEach((t) => {
      if (!t.is_open || !t.market_index) return;
      margin += parseFloat(t.collateral_amount) / (10 ** (process.env.REACT_APP_USDC_DECIMALS || 6));
    });
    return { totalMargin: margin, tradeCount: count };
  }, [openTrades]);

  // Unrealized PnL comes from the keeper's latest pendingPnlUSDCumulative — no extra RPC calls
  const unrealizedPnl = portfolioStats?.unrealizedPnl ?? 0;
  const unrealizedIsProfit = unrealizedPnl >= 0;
  const unrealizedPct = totalMargin > 0 ? (unrealizedPnl / totalMargin) * 100 : 0;

  // ─── Fetch portfolio stats from keeper ───
  const fetchPortfolioStats = useCallback(async () => {
    if (!account) return;
    setLoading(true);
    try {
      const config = getReferralConfig();
      // Support both EVM (0x...) and Cosmos (nibi1...) addresses
      const nibiAddr = account.startsWith("0x")
        ? evmToNibiAddress(account)
        : account;

      const query = `
        query PortfolioStats($trader: String!, $range: String!) {
          perp {
            statsUserPortfolio(trader: $trader, range: $range) {
              timestamp
              realizedPnlUSD
              realizedPnlUSDCumulative
              pendingPnlUSDCumulative
              volumeUSD
              volumeUSDCumulative
              tradesCount
              tradesCountCumulative
            }
          }
        }
      `;

      const res = await fetch(config.keeperUrl, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ query, variables: { trader: nibiAddr, range: chartRange } }),
      });

      const json = await res.json();
      const stats = json.data?.perp?.statsUserPortfolio ?? [];

      if (stats.length > 0) {
        const last = stats[stats.length - 1];
        setPortfolioStats({
          realizedPnl: last.realizedPnlUSDCumulative,
          unrealizedPnl: last.pendingPnlUSDCumulative,
          totalVolume: last.volumeUSDCumulative,
          totalTrades: last.tradesCountCumulative,
        });

        setChartData(
          stats.map((s) => ({
            date: s.timestamp,
            realized: Number(s.realizedPnlUSDCumulative.toFixed(4)),
            unrealized: Number(s.pendingPnlUSDCumulative.toFixed(4)),
            dailyRealized: Number(s.realizedPnlUSD.toFixed(4)),
            volume: Number(s.volumeUSD.toFixed(2)),
          }))
        );
      }
    } catch (err) {
      console.error("Failed to fetch portfolio stats:", err);
    } finally {
      setLoading(false);
    }
  }, [account, chartRange]);

  useEffect(() => {
    fetchPortfolioStats();
  }, [fetchPortfolioStats]);

  // ─── Fetch wallet USDC balance + current vault deposit value ───
  const fetchWalletData = useCallback(async () => {
    if (!account) return;
    setWalletLoading(true);
    try {
      const isEvm = account.startsWith("0x");
      const [balance, deposits] = await Promise.all([
        getUserTokenBalance(account, getCollateralDenom(), isEvm),
        getUserVaultDeposits(account, isEvm),
      ]);
      setWalletBalance(parseFloat(balance) || 0);
      const depositValue = (deposits || []).reduce((t, d) => t + (parseFloat(d.currentValue) || 0), 0);
      setVaultDeposits(depositValue);
    } catch (err) {
      console.error("Failed to fetch wallet/vault data:", err);
    } finally {
      setWalletLoading(false);
    }
  }, [account]);

  useEffect(() => {
    fetchWalletData();
  }, [fetchWalletData]);

  // ─── Chart range buttons ───
  const ranges = ["7D", "30D", "ALL"];

  // ─── Derived values ───
  const realizedPnl = portfolioStats?.realizedPnl ?? 0;
  const totalVolume = portfolioStats?.totalVolume ?? 0;
  const realizedIsProfit = realizedPnl >= 0;

  // Show whenever the user has trades, portfolio history, OR any wallet/vault balance
  // (so balance + vault-deposit cards still render for non-traders).
  const hasWalletData = (walletBalance ?? 0) > 0 || (vaultDeposits ?? 0) > 0;

  // First load: neither fetch has answered yet, so there is nothing to decide
  // the "does this user have anything to show" question with. Rendering null
  // here meant the trades table sat at the top of the page and then got shoved
  // down when the keeper replied — the skeleton holds that space instead.
  if (!portfolioStats && (loading || walletLoading)) return <OverviewSkeleton />;

  if (openTrades.length === 0 && !portfolioStats && !hasWalletData) return null;

  return (
    <div className="mb-6 space-y-4">
      {/* ─── Header ─── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[15px] font-bold" style={{ color: "var(--ink)" }}>Portfolio Overview</h2>
          <p className="text-[11px] mt-0.5" style={{ color: "var(--ink-3)" }}>
            {tradeCount > 0 ? `${tradeCount} open position${tradeCount !== 1 ? "s" : ""}` : "No open positions"}
            {portfolioStats?.totalTrades ? ` \u00b7 ${portfolioStats.totalTrades} total trades` : ""}
          </p>
        </div>
        {/* Balance + Vault Deposits (inline, right side) */}
        <div className="flex items-center gap-5 sm:gap-7">
          <div className="text-right">
            <div className="flex items-center justify-end gap-1 text-[11px]" style={{ color: "var(--ink-3)" }}>
              Balance
              <InfoTip content="Your available USDC wallet balance (not locked in positions or vaults)" />
            </div>
            <div className="text-[14px] sm:text-[15px] font-bold ce-figure" style={{ color: "var(--ink)" }}>
              {walletLoading ? "—" : `$${formatCurrency(walletBalance ?? 0)}`}
            </div>
          </div>
          <div
            onClick={() => navigate("/perps/deposits")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter") navigate("/perps/deposits"); }}
            className="text-right cursor-pointer group"
          >
            <div className="flex items-center justify-end gap-1 text-[11px] transition-colors" style={{ color: "var(--ink-3)" }}>
              Vault Deposits
              <InfoTip content="Current value of your liquidity vault deposits. Click to manage." />
            </div>
            <div className="flex items-center justify-end gap-1 text-[14px] sm:text-[15px] font-bold ce-figure" style={{ color: "var(--cat-accent)" }}>
              {walletLoading ? "—" : `$${formatCurrency(vaultDeposits ?? 0)}`}
              <svg width="11" height="11" viewBox="0 0 10 10" fill="none" className="group-hover:translate-x-0.5 transition-transform">
                <path d="M3.5 2L7 5L3.5 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Stats Grid ─── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {/* Unrealized PnL */}
        <StatCard label="Unrealized PnL" tooltip="Current profit/loss on open positions before closing fees. Sourced from on-chain data.">
          {loading ? <ValueSkeleton /> : (
            <div className="flex items-center gap-1.5">
              {unrealizedIsProfit ? <TrendUpIcon /> : <TrendDownIcon />}
              <span className="text-[20px] sm:text-[22px] font-bold ce-figure" style={{ color: unrealizedIsProfit ? "var(--up)" : "var(--down)" }}>
                {unrealizedIsProfit ? "" : "-"}${formatCurrency(Math.abs(unrealizedPnl))}
              </span>
            </div>
          )}
          {!loading && totalMargin > 0 && (
            <p className="text-[11px] mt-1" style={{ color: unrealizedIsProfit ? "var(--up)" : "var(--down)" }}>
              {unrealizedIsProfit ? "+" : "-"}{formatPct(unrealizedPct)}% on margin
            </p>
          )}
        </StatCard>

        {/* Realized PnL */}
        <StatCard label="Realized PnL" tooltip="Total profit/loss from closed trades, after all fees (open, close, borrowing)">
          {loading ? <ValueSkeleton /> : (
            <div className="flex items-center gap-1.5">
              {realizedIsProfit ? <TrendUpIcon /> : <TrendDownIcon />}
              <span className="text-[20px] sm:text-[22px] font-bold ce-figure" style={{ color: realizedIsProfit ? "var(--up)" : "var(--down)" }}>
                {realizedIsProfit ? "" : "-"}${formatCurrency(Math.abs(realizedPnl))}
              </span>
            </div>
          )}
          <p className="text-[11px] mt-1" style={{ color: "var(--ink-3)" }}>All time</p>
        </StatCard>

        {/* Total Margin */}
        <StatCard label="Total Margin" tooltip="Total collateral locked in your open positions">
          <span className="text-[20px] sm:text-[22px] font-bold ce-figure" style={{ color: "var(--cat-accent)" }}>
            ${formatCurrency(totalMargin)}
          </span>
          <p className="text-[11px] mt-1" style={{ color: "var(--ink-3)" }}>Invested</p>
        </StatCard>

        {/* Volume */}
        <StatCard label="Volume" tooltip="Total trading volume across all your trades">
          {loading ? <ValueSkeleton /> : (
            <span className="text-[20px] sm:text-[22px] font-bold ce-figure" style={{ color: "var(--ink)" }}>
              ${formatCurrency(totalVolume)}
            </span>
          )}
          <p className="text-[11px] mt-1" style={{ color: "var(--ink-3)" }}>All time</p>
        </StatCard>
      </div>

      {/* ─── PnL Chart ─── */}
      <div
        className="px-4 sm:px-5 pt-4 pb-2"
        style={{
          background: "var(--surface)",
          borderRadius: "var(--r-card)",
          boxShadow: "var(--card)",
        }}
      >
        {/* Chart header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-4">
            <h3 className="text-[13px] font-semibold" style={{ color: "var(--ink)" }}>PnL History</h3>
            <div className="flex items-center gap-3 text-[11px]" style={{ color: "var(--ink-3)" }}>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full" style={{ background: "var(--cat-accent)" }} /> Realized
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full" style={{ background: "var(--up)" }} /> Unrealized
              </span>
            </div>
          </div>
          <div className="ce-seg" style={{ height: 30 }}>
            {ranges.map((r) => (
              <button
                key={r}
                type="button"
                role="tab"
                aria-selected={chartRange === r}
                onClick={() => setChartRange(r)}
                className="ce-seg-item"
                style={{
                  padding: "0 12px",
                  height: 22,
                  fontSize: "11px",
                  ...(chartRange === r
                    ? { background: "var(--raise)", boxShadow: "var(--pillow-sm)", color: "var(--cat-accent)" }
                    : null),
                }}
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        {/* Chart */}
        <div className="h-[200px] sm:h-[240px]">
          {loading ? (
            <div className="w-full h-full flex items-center justify-center">
              <div
                className="animate-spin rounded-full h-6 w-6"
                style={{ border: "2px solid var(--line-2)", borderTopColor: "var(--cat-accent)" }}
              />
            </div>
          ) : chartData.length === 0 ? (
            <div className="w-full h-full flex items-center justify-center">
              <p className="text-[13px]" style={{ color: "var(--ink-3)" }}>No trading history yet</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradRealized" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={palette.accent} stopOpacity={0.2} />
                    <stop offset="100%" stopColor={palette.accent} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradUnrealized" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={palette.up} stopOpacity={0.15} />
                    <stop offset="100%" stopColor={palette.up} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={palette.grid} vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  tick={{ fontSize: 10, fill: palette.axis }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                  minTickGap={40}
                />
                <YAxis
                  tickFormatter={(v) => `$${v >= 0 ? "" : "-"}${formatCurrency(Math.abs(v))}`}
                  tick={{ fontSize: 10, fill: palette.axis }}
                  axisLine={false}
                  tickLine={false}
                  width={55}
                />
                <RechartsTooltip
                  content={<ChartTooltipContent />}
                  cursor={{ stroke: palette.grid, strokeDasharray: "4 4" }}
                />
                <Area
                  type="monotone"
                  dataKey="realized"
                  stroke={palette.accent}
                  strokeWidth={2}
                  fill="url(#gradRealized)"
                  dot={false}
                  activeDot={{ r: 4, stroke: palette.accent, strokeWidth: 2, fill: palette.dot }}
                  animationDuration={800}
                  animationEasing="ease-out"
                />
                <Area
                  type="monotone"
                  dataKey="unrealized"
                  stroke={palette.up}
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  fill="url(#gradUnrealized)"
                  dot={false}
                  activeDot={{ r: 3, stroke: palette.up, strokeWidth: 2, fill: palette.dot }}
                  animationDuration={1000}
                  animationEasing="ease-out"
                  animationBegin={200}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default TotalPnL;
